import React from 'react';
import type { ReactNode } from 'react';
import KBArticleSection from './KBArticleSection';

// Main responsive container for all frames
interface ResponsiveContainerProps {
  children: ReactNode;
  className?: string;
  background?: 'stone' | 'gradient';
}

export const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({ 
  children, 
  className = '', 
  background = 'stone' 
}) => {
  const bgClass = background === 'gradient' 
    ? 'bg-gradient-to-b from-cyan-900 to-slate-900' 
    : 'bg-stone-50';
    
  return (
    <div className={`min-h-screen w-full ${bgClass} flex flex-col ${className}`}>
      {children}
    </div>
  );
};

// Main content area with responsive padding
interface ResponsiveContentProps {
  children: ReactNode;
  className?: string;
}

export const ResponsiveContent: React.FC<ResponsiveContentProps> = ({ 
  children, 
  className = '' 
}) => {
  return (
    <div className={`flex-1 w-full max-w-7xl mx-auto px-10 sm:px-16 lg:px-24 py-8 relative ${className}`}>
        {children}
        <KBArticleSection />
      </div>
  );
};

// Responsive footer navigation
interface ResponsiveFooterProps {
  title: string;
  subtitle: string;
  progressPercent?: number;
  onNext?: () => void;
  onBack?: () => void;
  nextDisabled?: boolean;
  nextText?: string;
  backText?: string;

  nextNode?: ReactNode;
  backNode?: ReactNode;
}

export const ResponsiveFooter: React.FC<ResponsiveFooterProps> = ({
  title,
  subtitle,
  progressPercent = 20,
  onNext,
  onBack,
  nextDisabled = false,
  nextText = "Next →",
  backText = "← Back",

  nextNode,
  backNode,
}) => {
  return (
    <footer className="w-full bg-stone-50 border-t border-stone-200 pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-cyan-900 rounded-md shadow-lg mx-4 mb-4 p-6">
          <div className="flex flex-col sm:flex-row justify-start items-start sm:items-start gap-4">
            
            {/* Footer Content */}
            <div className="flex-1 min-w-0 text-left">
              <h3 className="text-stone-50 text-lg font-medium font-['Work_Sans'] leading-snug sm:text-xl sm:leading-relaxed mb-2 text-left">
                {title}
              </h3>
              <p className="text-stone-50/50 text-[11px] font-normal font-['Work_Sans'] leading-snug text-left sm:text-xs sm:leading-none">
                {subtitle}
              </p>
              
              {/* Progress bar */}
              <div className="mt-4 w-full max-w-2xl">
                <div className="w-full h-[5px] bg-stone-400 rounded-md relative">
                  <div 
                    className="h-[5px] bg-green-900 rounded-md absolute left-0 top-0 transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            </div>
            
            {/* Navigation Buttons: one row on all breakpoints; min-w-0 + wrap avoids ellipsis on long labels */}
            <div className="flex w-full min-w-0 flex-row gap-2 sm:w-auto sm:gap-3">
              {/* Back */}
              {backNode ? (
                backNode
              ) : onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex min-h-[44px] min-w-0 flex-1 items-center justify-center gap-2 rounded border border-stone-50 bg-transparent px-2 py-2 text-stone-50 transition-colors hover:bg-stone-50/10 sm:flex-initial sm:px-6 sm:py-3"
                >
                  <span className="text-center text-[11px] font-medium font-['Work_Sans'] leading-snug text-stone-50 sm:text-sm md:text-base">
                    {backText}
                  </span>
                </button>
              ) : null}
              
              {/* Next */}
              {nextNode ? (
                nextNode
              ) : onNext ? (
                <button
                  type="button"
                  onClick={onNext}
                  disabled={nextDisabled}
                  className={`inline-flex min-h-[44px] min-w-0 flex-1 items-center justify-center gap-2 rounded px-2 py-2 transition-colors sm:flex-initial sm:px-6 sm:py-3 ${
                    nextDisabled
                      ? 'bg-cyan-700 opacity-50 cursor-not-allowed'
                      : 'bg-cyan-700 hover:bg-cyan-800 cursor-pointer opacity-100'
                  }`}
                >
                  <span className="text-center text-[11px] font-medium font-['Work_Sans'] leading-snug text-stone-50 sm:text-sm md:text-base">
                    {nextText}
                  </span>
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Responsive page header
interface ResponsiveHeaderProps {
  step?: string;
  title: string;
  onClose?: () => void;
  className?: string;
}

export const ResponsiveHeader: React.FC<ResponsiveHeaderProps> = ({
  step,
  title,
  onClose,
  className = ''
}) => {
  return (
    <div className={`w-full md:mb-8 mb-2${className}`}>
      {onClose && (
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-6 h-6 bg-stone-500 hover:bg-stone-600 transition-colors rounded"
        />
      )}
      
      {step && (
        <div className="text-black/50 text-xs font-normal font-['Work_Sans'] leading-3 mb-2">
          {step}
        </div>
      )}
      
      <h1 className="text-cyan-900 text-2xl sm:text-3xl font-semibold font-['Test_Domaine_Display'] leading-loose">
        {title}
      </h1>
    </div>
  );
};

// Responsive chat button
export const ResponsiveChatButton: React.FC = () => {
  return null;
}; 